/**
 * PatternIQ Admin - Tenant Agent Configurations
 * ==============================================
 * Phase C1: White-label agent customization UI
 * 
 * Allows admins to customize agent behavior per tenant:
 * - System prompt additions
 * - Tool restrictions
 * - Confidence thresholds
 */

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Bot, 
  Settings, 
  Save, 
  RotateCcw, 
  ChevronDown,
  ChevronUp,
  Wrench,
  MessageSquare,
  Gauge,
  CheckCircle,
  AlertCircle,
  X
} from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Textarea } from '../../components/ui/textarea';
import { Slider } from '../../components/ui/slider';
import { Checkbox } from '../../components/ui/checkbox';
import { Label } from '../../components/ui/label';

interface AgentOverrides {
  systemPromptAppend?: string;
  toolAllowlist?: string[];
  maxDepth?: number;
  minConfidenceForActions?: number;
  kbBoostTags?: string[];
  displayName?: string;
  displayDescription?: string;
}

interface AgentWithConfig {
  agentId: string;
  agentName: string;
  agentVersion: string;
  hasOverrides: boolean;
  overrides: AgentOverrides;
  availableTools: string[];
}

interface ListResponse {
  success: boolean;
  companyId: string;
  agents: AgentWithConfig[];
  totalAgents: number;
  agentsWithOverrides: number;
}

export default function TenantAgents() {
  const queryClient = useQueryClient();
  const [expandedAgent, setExpandedAgent] = useState<string | null>(null);
  const [editingOverrides, setEditingOverrides] = useState<Record<string, AgentOverrides>>({});
  const [saveStatus, setSaveStatus] = useState<{ agentId: string; status: 'success' | 'error'; message: string } | null>(null);

  // Fetch all agent configs
  const { data, isLoading, error } = useQuery<ListResponse>({
    queryKey: ['tenant-agent-configs'],
    queryFn: async () => {
      const res = await fetch('/api/tenant-agents/list');
      if (!res.ok) throw new Error('Failed to fetch agent configs');
      return res.json();
    }
  });

  // Initialize editing state when data loads
  useEffect(() => {
    if (data?.agents) {
      const initial: Record<string, AgentOverrides> = {};
      data.agents.forEach(agent => {
        initial[agent.agentId] = agent.overrides || {};
      });
      setEditingOverrides(initial);
    }
  }, [data]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async ({ agentId, overrides }: { agentId: string; overrides: AgentOverrides }) => {
      const res = await fetch('/api/tenant-agents/save', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ agentId, overrides })
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to save');
      }
      return res.json();
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tenant-agent-configs'] });
      setSaveStatus({ agentId: variables.agentId, status: 'success', message: 'Configuration saved!' });
      setTimeout(() => setSaveStatus(null), 3000);
    },
    onError: (error: Error, variables) => {
      setSaveStatus({ agentId: variables.agentId, status: 'error', message: error.message });
      setTimeout(() => setSaveStatus(null), 5000);
    }
  });

  // Reset mutation
  const resetMutation = useMutation({
    mutationFn: async (agentId: string) => {
      const res = await fetch(`/api/tenant-agents/delete?agentId=${agentId}`, {
        method: 'DELETE'
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to reset');
      }
      return res.json();
    },
    onSuccess: (_, agentId) => {
      queryClient.invalidateQueries({ queryKey: ['tenant-agent-configs'] });
      setEditingOverrides(prev => ({ ...prev, [agentId]: {} }));
      setSaveStatus({ agentId, status: 'success', message: 'Reset to defaults!' });
      setTimeout(() => setSaveStatus(null), 3000);
    }
  });

  const handleOverrideChange = (agentId: string, field: keyof AgentOverrides, value: any) => {
    setEditingOverrides(prev => ({
      ...prev,
      [agentId]: {
        ...prev[agentId],
        [field]: value
      }
    }));
  };

  const handleToolToggle = (agentId: string, tool: string, checked: boolean) => {
    const currentAllowlist = editingOverrides[agentId]?.toolAllowlist || [];
    let newAllowlist: string[];
    
    if (checked) {
      newAllowlist = [...currentAllowlist, tool];
    } else {
      newAllowlist = currentAllowlist.filter(t => t !== tool);
    }
    
    handleOverrideChange(agentId, 'toolAllowlist', newAllowlist.length > 0 ? newAllowlist : undefined);
  };

  const handleSave = (agentId: string) => {
    const overrides = editingOverrides[agentId];
    // Clean up empty values
    const cleanedOverrides: AgentOverrides = {};
    if (overrides.systemPromptAppend?.trim()) {
      cleanedOverrides.systemPromptAppend = overrides.systemPromptAppend.trim();
    }
    if (overrides.toolAllowlist && overrides.toolAllowlist.length > 0) {
      cleanedOverrides.toolAllowlist = overrides.toolAllowlist;
    }
    if (overrides.minConfidenceForActions !== undefined && overrides.minConfidenceForActions !== 0.7) {
      cleanedOverrides.minConfidenceForActions = overrides.minConfidenceForActions;
    }
    if (overrides.displayName?.trim()) {
      cleanedOverrides.displayName = overrides.displayName.trim();
    }
    
    saveMutation.mutate({ agentId, overrides: cleanedOverrides });
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">Failed to load agent configurations</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Bot className="w-7 h-7 text-purple-600" />
            Agent Configurations
          </h1>
          <p className="text-gray-600 mt-1">
            Customize agent behavior for your organization
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-sm">
            {data?.agentsWithOverrides || 0} / {data?.totalAgents || 0} customized
          </Badge>
        </div>
      </div>

      {/* Agent Cards */}
      <div className="space-y-4">
        {data?.agents.map(agent => {
          const isExpanded = expandedAgent === agent.agentId;
          const currentOverrides = editingOverrides[agent.agentId] || {};
          const hasChanges = JSON.stringify(currentOverrides) !== JSON.stringify(agent.overrides);
          
          return (
            <Card key={agent.agentId} className={`transition-all ${agent.hasOverrides ? 'border-purple-200 bg-purple-50/30' : ''}`}>
              <CardHeader 
                className="cursor-pointer"
                onClick={() => setExpandedAgent(isExpanded ? null : agent.agentId)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Bot className={`w-5 h-5 ${agent.hasOverrides ? 'text-purple-600' : 'text-gray-400'}`} />
                    <div>
                      <CardTitle className="text-lg">{agent.agentName}</CardTitle>
                      <CardDescription className="text-sm">
                        v{agent.agentVersion} • {agent.availableTools.length} tools available
                      </CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {agent.hasOverrides && (
                      <Badge variant="secondary" className="bg-purple-100 text-purple-700">
                        Customized
                      </Badge>
                    )}
                    {saveStatus?.agentId === agent.agentId && (
                      <Badge 
                        variant={saveStatus.status === 'success' ? 'default' : 'destructive'}
                        className="animate-in fade-in"
                      >
                        {saveStatus.status === 'success' ? (
                          <CheckCircle className="w-3 h-3 mr-1" />
                        ) : (
                          <AlertCircle className="w-3 h-3 mr-1" />
                        )}
                        {saveStatus.message}
                      </Badge>
                    )}
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>
              </CardHeader>

              {isExpanded && (
                <CardContent className="space-y-6 pt-0">
                  {/* System Prompt Append */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <MessageSquare className="w-4 h-4" />
                      Additional Instructions
                    </Label>
                    <Textarea
                      placeholder="Add custom instructions for this agent (e.g., 'Always use formal tone', 'Prioritize inventory metrics')"
                      value={currentOverrides.systemPromptAppend || ''}
                      onChange={(e) => handleOverrideChange(agent.agentId, 'systemPromptAppend', e.target.value)}
                      rows={3}
                      className="font-mono text-sm"
                    />
                    <p className="text-xs text-gray-500">
                      These instructions will be appended to the agent's system prompt
                    </p>
                  </div>

                  {/* Confidence Threshold */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Gauge className="w-4 h-4" />
                      Action Confidence Threshold: {((currentOverrides.minConfidenceForActions || 0.7) * 100).toFixed(0)}%
                    </Label>
                    <Slider
                      value={[(currentOverrides.minConfidenceForActions || 0.7) * 100]}
                      onValueChange={(value) => handleOverrideChange(agent.agentId, 'minConfidenceForActions', value[0] / 100)}
                      min={50}
                      max={95}
                      step={5}
                      className="w-full max-w-md"
                    />
                    <p className="text-xs text-gray-500">
                      Minimum confidence required before agent proposes actions
                    </p>
                  </div>

                  {/* Tool Allowlist */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Wrench className="w-4 h-4" />
                      Allowed Tools
                      {currentOverrides.toolAllowlist && currentOverrides.toolAllowlist.length > 0 && (
                        <Badge variant="outline" className="text-xs">
                          {currentOverrides.toolAllowlist.length} / {agent.availableTools.length} selected
                        </Badge>
                      )}
                    </Label>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-48 overflow-y-auto p-2 border rounded-md bg-gray-50">
                      {agent.availableTools.map(tool => {
                        const isAllowed = !currentOverrides.toolAllowlist || 
                          currentOverrides.toolAllowlist.length === 0 ||
                          currentOverrides.toolAllowlist.includes(tool);
                        
                        return (
                          <div key={tool} className="flex items-center space-x-2">
                            <Checkbox
                              id={`${agent.agentId}-${tool}`}
                              checked={isAllowed}
                              onCheckedChange={(checked) => {
                                if (currentOverrides.toolAllowlist && currentOverrides.toolAllowlist.length > 0) {
                                  handleToolToggle(agent.agentId, tool, checked as boolean);
                                } else {
                                  // First restriction - start with all tools except this one
                                  const allExceptThis = agent.availableTools.filter(t => t !== tool);
                                  handleOverrideChange(agent.agentId, 'toolAllowlist', allExceptThis);
                                }
                              }}
                            />
                            <label
                              htmlFor={`${agent.agentId}-${tool}`}
                              className="text-xs font-mono cursor-pointer truncate"
                              title={tool}
                            >
                              {tool}
                            </label>
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-xs text-gray-500">
                      Leave all checked for default behavior. Uncheck tools to restrict agent access.
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 pt-4 border-t">
                    <Button
                      onClick={() => handleSave(agent.agentId)}
                      disabled={saveMutation.isPending}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {saveMutation.isPending ? 'Saving...' : 'Save Configuration'}
                    </Button>
                    {agent.hasOverrides && (
                      <Button
                        variant="outline"
                        onClick={() => resetMutation.mutate(agent.agentId)}
                        disabled={resetMutation.isPending}
                      >
                        <RotateCcw className="w-4 h-4 mr-2" />
                        Reset to Defaults
                      </Button>
                    )}
                    {hasChanges && (
                      <Badge variant="outline" className="text-yellow-600 border-yellow-300">
                        Unsaved changes
                      </Badge>
                    )}
                  </div>
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>

      {/* Help Section */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <h3 className="font-semibold text-blue-900 mb-2">About Agent Configurations</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• <strong>Additional Instructions:</strong> Custom prompts appended to agent behavior</li>
            <li>• <strong>Confidence Threshold:</strong> Higher values = more conservative action proposals</li>
            <li>• <strong>Tool Restrictions:</strong> Limit which data sources an agent can access</li>
            <li>• Changes take effect immediately for new conversations</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}

